import { useState, useRef, useEffect } from "react";
import { getNodeExplanation } from "../../api";
import type { Graph3DNode } from "../../types";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface Props {
  selectedNode: Graph3DNode | null;
}

export default function AIExplainButton({ selectedNode }: Props) {
  const [open, setOpen] = useState(false);
  const [explanation, setExplanation] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleClick = async () => {
    if (!selectedNode) return;
    setOpen(true);
    setExplanation("");
    setError("");
    setLoading(true);
    try {
      const result = await getNodeExplanation(selectedNode.id);
      setExplanation(result);
    } catch (e) {
      setError("설명을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // Scroll to bottom when explanation changes
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [explanation, open]);

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={handleClick}
        disabled={!selectedNode}
        className={`fixed bottom-8 right-8 z-50 px-6 py-3 rounded-full shadow-lg text-white text-lg font-semibold transition-colors ${
          !selectedNode
            ? "bg-gray-400 cursor-not-allowed opacity-60"
            : "bg-emerald-500 hover:bg-emerald-600 cursor-pointer"
        }`}
      >
        AI에게 설명듣기
      </button>
      {/* Bottom Sheet */}
      {open && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          {/* Sheet */}
          <div className="relative bg-white rounded-t-2xl shadow-xl w-full max-h-[60vh] p-0 animate-slideup">
            {/* Sticky Header */}
            <div className="sticky top-0 z-10 bg-white rounded-t-2xl px-6 pt-6 pb-2 flex items-center justify-between border-b">
              <h2 className="text-lg font-bold">AI 설명</h2>
              <button
                className="text-2xl text-gray-400 hover:text-gray-600 ml-2"
                onClick={() => setOpen(false)}
                aria-label="닫기"
              >
                ×
              </button>
            </div>
            {/* Scrollable Content */}
            <div
              ref={scrollRef}
              className="px-6 pb-6 pt-2 w-full h-60 min-h-[180px] overflow-y-auto text-base leading-relaxed rounded-lg border border-gray-200 bg-gray-50"
            >
              {loading ? (
                <div className="text-center text-gray-500 py-8">
                  설명 불러오는 중...
                </div>
              ) : error ? (
                <div className="text-red-500 text-center py-8">{error}</div>
              ) : (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {explanation}
                </ReactMarkdown>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Slide up animation */}
      <style>{`
        .animate-slideup {
          animation: slideup 0.3s cubic-bezier(0.4,0,0.2,1);
        }
        @keyframes slideup {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </>
  );
}
